import firebase from "firebase/app";
import "firebase/functions";
import firestore from "src/apiService/firestore";
import { getUserId } from "src/config/storage";
import { USER_PLAN as UserPlan } from "src/enums/common.enum";
import type { Cookie } from "src/utils/extension";

type DepopAttributes = {
  department?: Array<{ id: string; name?: string }>;
  group?: Array<{
    id: string;
    name?: string;
    department?: string[];
    product_types?: Array<{
      id: string;
      name?: string;
      department?: string[];
    }>;
  }>;
};

export type DepopAttributesIndex = {
  departmentsById: Record<string, string>;
  groupsById: Record<
    string,
    {
      id: string;
      name: string;
      department: string[];
      productTypesById: Record<
        string,
        { id: string; name: string; department: string[] }
      >;
    }
  >;
};

let depopAttributesPromise: Promise<DepopAttributes> | null = null;

export function clearDepopAttributesCache() {
  depopAttributesPromise = null;
}

async function fetchDepopAttributes(): Promise<DepopAttributes> {
  if (!depopAttributesPromise) {
    depopAttributesPromise = firebase
      .functions()
      .httpsCallable("depop-getAttributesIndex")()
      .then((res) => {
        const data = res.data as { attributes?: DepopAttributes };
        return data.attributes || { department: [], group: [] };
      })
      .catch((error) => {
        depopAttributesPromise = null;
        throw error;
      });
  }
  return await depopAttributesPromise;
}

export async function getDepopAttributesIndex(): Promise<DepopAttributesIndex> {
  const attributes = await fetchDepopAttributes();
  const departmentsById: Record<string, string> = {};
  for (const entry of attributes?.department || []) {
    if (entry?.id) departmentsById[entry.id] = entry.name || "";
  }

  const groupsById: DepopAttributesIndex["groupsById"] = {};
  for (const entry of attributes?.group || []) {
    if (!entry?.id) continue;
    const productTypesById: Record<
      string,
      { id: string; name: string; department: string[] }
    > = {};
    for (const productType of entry.product_types || []) {
      if (!productType?.id) continue;
      productTypesById[productType.id] = {
        id: productType.id,
        name: productType.name || "",
        department: Array.isArray(productType.department)
          ? productType.department
          : [],
      };
    }
    groupsById[entry.id] = {
      id: entry.id,
      name: entry.name || "",
      department: Array.isArray(entry.department) ? entry.department : [],
      productTypesById,
    };
  }

  return { departmentsById, groupsById };
}

export async function depopIntegrationCreate(args: {
  cookies: Cookie[];
  integrationId?: string;
  username: string;
  userId: string;
  lastSync?: string;
  bankAccountId?: string;
  userAgent?: string;
}) {
  const f = firebase.functions().httpsCallable("depop-integrationCreate");
  const { userAgent = navigator.userAgent, ...rest } = args;
  return await f({ ...rest, userAgent });
}

export async function depopIntegrationDelete(id: string) {
  const f = firebase.functions().httpsCallable("depop-deleteIntegration");
  return await f({ integrationId: id });
}

export async function depopIntegrationSyncChange(
  id: string,
  sync: boolean,
  inventory: boolean = true
) {
  const userId = await getUserId();
  const db = firestore();

  await db.runTransaction(async (transaction) => {
    const user = await transaction.get(db.collection("Users").doc(userId));
    await transaction.update(db.collection("Plaid_Integrations").doc(id), {
      sync: sync && user.data()?.plan === UserPlan.ULTIMATE,
      inventory: sync && inventory,
    });
  });
}

export async function depopProcessFile(
  integrationId: string,
  type: "sales" | "inventory",
  importType: "initial-import" | "direct-import" | "daily-sync",
  filename: string,
  {
    uploadSalesId,
    uploadReturnsId,
    uploadInventoryId,
  }: {
    uploadSalesId?: string;
    uploadReturnsId?: string;
    uploadInventoryId?: string;
  } = {}
) {
  const f = firebase.functions().httpsCallable("depop-processFile");
  return await f({
    integrationId,
    importType,
    type,
    filename,
    uploadSalesId,
    uploadReturnsId,
    uploadInventoryId,
  });
}

export async function getDepopIntegration() {
  const uid = await getUserId();
  const db = firestore();
  const integrations = await db
    .collection("Plaid_Integrations")
    .where("user", "==", uid)
    .where("type", "==", "depop")
    .get();
  const integration = integrations.docs.pop();
  if (integration) {
    return integration.data();
  }
  return null;
}
